import React, { useState, Suspense } from 'react';
import { Canvas } from '@react-three/fiber';
import { AnimatePresence, motion } from 'framer-motion';
import { Scene } from './components/3d/Scene';
import { InfoOverlay } from './components/UI/InfoOverlay';
import { SimulationOverlay } from './components/UI/SimulationOverlay';
import { FloatingHeader } from './components/UI/FloatingHeader';
import { ContributionSection } from './components/UI/ContributionSection';
import { ChapterDetails } from './components/UI/ChapterDetails';
import { VideoOverview } from './components/UI/VideoOverview';
import { ThesisOverview } from './components/UI/ThesisOverview';
import { Navigation } from './components/UI/Navigation';
import ErrorBoundary from './components/UI/ErrorBoundary';
import { CanvasLoader } from './components/UI/CanvasLoader';
import { ncrfData } from './data/ncrfData';
import { evaluationData } from './data/evaluationData';
import simulationData from './data/simulation_results.json';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './components/Auth/Login';

// Inner App component that uses auth context
const AuthenticatedApp = () => {
  const { isAuthenticated, loading } = useAuth();
  const [selectedId, setSelectedId] = useState(null);
  const [viewMode, setViewMode] = useState('framework'); // 'framework', 'evaluation', 'simulation'
  const [showSimulationDetails, setShowSimulationDetails] = useState(false);

  const currentData = viewMode === 'framework' ? ncrfData : evaluationData;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center text-white">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading...</p>
        </motion.div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Login />;
  }

  return (
    <div className="relative w-full bg-white">
      {/* Skip to main content link for accessibility */}
      <a 
        href="#thesis-overview" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-blue-600 focus:text-white focus:rounded-lg"
      >
        Skip to main content
      </a>

      <Navigation />

      {/* Hero Section with 3D Model */}
      <section 
        className="relative w-full h-screen bg-gradient-to-b from-gray-50 to-white overflow-hidden"
        aria-label="Interactive 3D Framework Visualization"
      >
        <FloatingHeader />

        {/* View Switcher */}
        <div 
          className="absolute top-24 left-1/2 transform -translate-x-1/2 z-20 bg-white/80 backdrop-blur-md p-1 rounded-full shadow-lg border border-gray-200 flex"
          role="tablist"
          aria-label="View selection"
        >
          <button
            role="tab"
            aria-selected={viewMode === 'framework'}
            aria-controls="3d-view"
            onClick={() => { setViewMode('framework'); setSelectedId(null); setShowSimulationDetails(false); }}
            className={`px-6 py-2 rounded-full text-sm font-bold transition-all ${viewMode === 'framework' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-500 hover:text-gray-800'}`}
          >
            Framework
          </button>
          <button
            role="tab"
            aria-selected={viewMode === 'evaluation'}
            aria-controls="3d-view"
            onClick={() => { setViewMode('evaluation'); setSelectedId(null); setShowSimulationDetails(false); }}
            className={`px-6 py-2 rounded-full text-sm font-bold transition-all ${viewMode === 'evaluation' ? 'bg-purple-600 text-white shadow-md' : 'text-gray-500 hover:text-gray-800'}`}
          >
            Evaluation Results
          </button>
          <button
            role="tab"
            aria-selected={viewMode === 'simulation'}
            aria-controls="3d-view"
            onClick={() => { setViewMode('simulation'); setSelectedId(null); }}
            className={`px-6 py-2 rounded-full text-sm font-bold transition-all ${viewMode === 'simulation' ? 'bg-green-600 text-white shadow-md' : 'text-gray-500 hover:text-gray-800'}`}
          >
            Simulation
          </button>
        </div>

        {/* 3D Canvas with Error Boundary and Loading State */}
        <ErrorBoundary fallbackMessage="The 3D visualization couldn't load. Your browser may not support WebGL, or there was an error rendering the scene.">
          <Suspense fallback={<CanvasLoader />}>
            <Canvas 
              id="3d-view"
              role="tabpanel"
              shadows 
              dpr={[1, 2]} 
              className="w-full h-full"
              gl={{ 
                antialias: true,
                failIfMajorPerformanceCaveat: false 
              }}
              onCreated={({ gl }) => {
                gl.setClearColor('#f9fafb', 1);
              }}
            >
              <Scene
                onSelect={setSelectedId}
                selectedId={selectedId}
                data={currentData}
                mode={viewMode}
                onShowSimulationDetails={() => setShowSimulationDetails(true)}
              />
            </Canvas>
          </Suspense>
        </ErrorBoundary>

        <AnimatePresence>
          {selectedId && (
            <InfoOverlay
              selectedId={selectedId}
              data={currentData}
              onClose={() => setSelectedId(null)}
              onNavigate={(mode) => {
                setViewMode(mode);
                setSelectedId(null);
                setShowSimulationDetails(false);
              }}
            />
          )}
          {showSimulationDetails && (
            <SimulationOverlay
              data={simulationData}
              onClose={() => setShowSimulationDetails(false)}
            />
          )}
        </AnimatePresence>

        {/* Hint for user */}
        {!selectedId && !showSimulationDetails && viewMode !== 'simulation' && (
          <div 
            className="absolute bottom-24 left-1/2 transform -translate-x-1/2 pointer-events-none text-center z-10"
            aria-hidden="true"
          >
            <p className="text-gray-400 text-xs font-bold uppercase tracking-widest animate-pulse mb-2">
              Click on a component to explore
            </p>
            <div className="w-px h-12 bg-gray-300 mx-auto"></div>
          </div>
        )}
      </section>

      {/* Content Sections */}
      <main>
        <section id="thesis-overview" className="bg-gray-50">
          <ThesisOverview />
        </section>

        <section id="contributions">
          <ContributionSection />
        </section>

        <section id="chapters">
          <ChapterDetails />
        </section>

        <section id="video-overview" className="bg-gray-50">
          <VideoOverview />
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-500 py-12 text-center border-t border-gray-800">
        <p className="text-sm">© 2025 Premnath K. Narayanan. All Rights Reserved.</p>
        <p className="text-xs text-gray-600 mt-2">
          PhD Thesis: Normative Reinforcement Control Framework for 5G Networks
        </p>
      </footer>
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <AuthenticatedApp />
    </AuthProvider>
  );
}

export default App;
